import React, { useEffect, useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useIdleTimer } from 'react-idle-timer/legacy'
import { useBeforeUnload } from 'utils/hooks/useBeforeUnload'
import { useCompareToPrev } from 'utils/hooks/useCompareToPrev'
import { useInterval } from 'utils/hooks/useInterval'
import { replaceInString } from 'utils/misc'
import { SECONDS_IN_MINUTE, MILLISECONDS_IN_SECOND, MAX_TIMEOUT_SECONDS } from 'utils/constants'
import STRINGS from 'utils/strings'

const isPastTimeLimit = (idleTimer, timeoutSeconds) => {
  const now = Date.now()
  const lastActiveMs = idleTimer.getLastActiveTime()
  const totalIdleMs = now - (lastActiveMs || now)
  const forcedTimeoutMs = (timeoutSeconds + SECONDS_IN_MINUTE) * MILLISECONDS_IN_SECOND
  return totalIdleMs > forcedTimeoutMs
}

const ModalContent = ({ closeModal, modal, onLogout, idleTimer, finalTimeoutSeconds }) => {
  const [remaining, setRemaining] = useState(SECONDS_IN_MINUTE)
  const hasModalStateChanged = useCompareToPrev(modal.open)
  const initialInactiveTimeMs = useRef(idleTimer.getLastActiveTime())

  useEffect(() => {
    if (hasModalStateChanged && !modal.open) setRemaining(SECONDS_IN_MINUTE)
    if (modal.open) initialInactiveTimeMs.current = idleTimer.getLastActiveTime()
  }, [modal.open, hasModalStateChanged, idleTimer])

  useInterval(() => {
    if (isPastTimeLimit(idleTimer, finalTimeoutSeconds)) {
      onLogout()
      closeModal()
    } else {
      const now = Date.now()
      const inactiveSeconds = Math.round((now - initialInactiveTimeMs.current) / 1000)
      let secondsLeft = finalTimeoutSeconds - inactiveSeconds
      if (secondsLeft < 1) secondsLeft = 1
      setRemaining(secondsLeft)
      if (remaining === 1) onLogout()
    }
  }, MILLISECONDS_IN_SECOND)

  return replaceInString(STRINGS.inactive_desc, '[##]', remaining)
}

const SessionTimeout = ({ onLogout, openModal, sessionTimeoutSeconds, modal }) => {
  const [isIdle, setIsIdle] = useState(false)
  const finalTimeoutSeconds = Math.min(sessionTimeoutSeconds, MAX_TIMEOUT_SECONDS)

  // Ensure we have a minimum timeout that allows for the warning modal
  const minimumTimeoutSeconds = Math.max(finalTimeoutSeconds, SECONDS_IN_MINUTE + 1)
  const idleTimeoutMs = (minimumTimeoutSeconds - SECONDS_IN_MINUTE) * MILLISECONDS_IN_SECOND

  const handleIdle = useCallback(() => {
    setIsIdle(true)

    // Check if user has already passed the absolute time limit
    if (isPastTimeLimit(idleTimer, finalTimeoutSeconds)) {
      onLogout()
      return
    }

    openModal({
      className: 'timeout',
      heading: STRINGS.inactive_title,
      children: (
        <ModalContent
          closeModal={handleCloseModal}
          modal={modal}
          onLogout={onLogout}
          idleTimer={idleTimer}
          finalTimeoutSeconds={finalTimeoutSeconds}
        />
      ),
      confirmButton: STRINGS.inactive_return,
      cancelButton: STRINGS.sign_out,
      closeOnBackgroundClick: true,
      humi: true,
      onConfirm: handleReset,
      onCancel: onLogout,
      onBackgroundClick: () => {},
    })
  }, [openModal, onLogout, finalTimeoutSeconds, modal, idleTimer])

  const handleActive = useCallback(() => {
    setIsIdle(false)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsIdle(false)
  }, [])

  const handleReset = useCallback(() => {
    reset()
    handleCloseModal()
  }, [reset, handleCloseModal])

  const idleTimer = useIdleTimer({
    timeout: idleTimeoutMs,
    onIdle: handleIdle,
    onActive: handleActive,
    debounce: 250,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'pointerdown',
      'pointermove',
    ],
    crossTab: {
      emitOnAllTabs: true,
    },
  })

  const { reset, getLastActiveTime } = idleTimer

  // Pass the getLastActiveTime method to the idleTimer object for use in isPastTimeLimit
  useEffect(() => {
    idleTimer.getLastActiveTime = getLastActiveTime
  }, [getLastActiveTime])

  useBeforeUnload(() => {
    try {
      const localUserData = JSON.parse(localStorage.getItem('user') || '{}')
      localStorage.setItem(
        'user',
        JSON.stringify({
          ...localUserData,
          last_active: Date.now().toString(),
        }),
      )
    } catch (error) {
      console.warn('Failed to save last active time:', error)
    }
  })

  useEffect(() => {
    const checkPreviousSession = () => {
      try {
        const localUserData = JSON.parse(localStorage.getItem('user') || '{}')
        if (localUserData?.last_active) {
          const now = Date.now()
          const elapsedSeconds = (now - Number(localUserData.last_active)) / 1000

          if (elapsedSeconds >= finalTimeoutSeconds) {
            onLogout()
            return
          }

          delete localUserData.last_active
          localStorage.setItem('user', JSON.stringify(localUserData))
        }
      } catch (error) {
        console.warn('Failed to check previous session:', error)
      }
    }

    checkPreviousSession()
  }, [onLogout, finalTimeoutSeconds])

  useEffect(() => {
    reset()
  }, [sessionTimeoutSeconds, reset])

  return null
}

SessionTimeout.propTypes = {
  modal: PropTypes.object,
  onLogout: PropTypes.func,
  openModal: PropTypes.func,
  sessionTimeoutSeconds: PropTypes.number,
}

export default SessionTimeout
